import type { HelpImage, HelpSection, KbFaq } from "./articles";

export type ArticleEN = {
  title: string;
  intro?: string;
  sections: HelpSection[];
  faq?: KbFaq[];
  updated?: string;
  related?: string[];
  /** EN alt/caption for the same src paths as RU (optional). */
  images?: HelpImage[];
};

/** Category ID → EN {title, description} */
export const KB_CATEGORIES_EN: Record<string, { title: string; description: string }> = {
  "getting-started": {
    title: "Getting Started",
    description: "Login, roles and access, period and organization selection, favorites and help.",
  },
  treasury: {
    title: "Treasury",
    description: "Payment requests, approvals, payment day, payment calendar.",
  },
  procurement: {
    title: "Procurement",
    description: "Purchase requests, supplier orders, receiving, invoices.",
  },
  contracts: {
    title: "Contracts",
    description: "Contract requests, approvals, register, income/expense contracts, budget vs. actual.",
  },
  budget: {
    title: "Budget Control",
    description: "Budget limits by cost center and line items, plan vs. actual, overruns, Excel import.",
  },
  reports: {
    title: "Management Accounting & Reports",
    description: "P&L, Cash Flow Statement, balance sheet, Budget of Income & Expenses (BDR), USALI hotel standard.",
  },
  registers: {
    title: "Registers & Data",
    description: "Turnover, inventory balances, accounts payable, Excel upload.",
  },
  integrations: {
    title: "Settings & Integrations",
    description: "Users and roles, approval routes, 1C integration, demo data.",
  },
};

/** Article slug → EN content */
export const KB_ARTICLES_EN: Record<string, ArticleEN> = {
  "getting-started": {
    title: "First Login and Interface Overview",
    intro:
      "How to log in, change your password, and get oriented in the interface in your first 5 minutes. Suitable for any new employee.",
    sections: [
      {
        heading: "Logging into the System",
        body: [
          "Your organization's administrator gives you access: you receive the system address and credentials (email and temporary password).",
          "On first login, the system will ask you to change your password—choose a strong one (at least 8 characters, uppercase and lowercase letters, and numbers).",
        ],
        steps: [
          "Open the system address in your browser (we recommend Chrome, Edge, or Yandex).",
          "Enter your email and temporary password, click \"Sign in\".",
          "Change your password when prompted.",
          "Check the organization name in the top right corner—this is your active legal entity.",
        ],
        tip: "If you forgot your password, use the \"Forgot password?\" link on the login page or contact your organization's administrator.",
      },
      {
        heading: "What's on the Screen",
        body: [
          "On the left is the main menu by section (Treasury, Procurement, Contracts, Budget Control, Registers, Reports, Settings). Menu contents depend on your role and plan.",
          "At the top are the section title, a ★ (favorites) button, ? (help) button, language switcher, notifications, and an organization switcher.",
          "On the bottom right is an assistant icon for scenario-specific tips.",
        ],
      },
      {
        heading: "What to Do Next",
        body: [
          "If you're an administrator—add employees and roles (Settings → Team & Roles), check legal entities and objects.",
          "If you don't have data yet—load it from Excel or connect 1C; for a demo, load demo data once (Settings → Demo Data).",
        ],
      },
    ],
    faq: [
      {
        q: "Which browser should I use?",
        a: "Any modern browser: Chrome, Edge, Yandex, Firefox, Safari. Internet Explorer is not supported.",
      },
      {
        q: "I don't see the section I need in the menu",
        a: "Menu contents depend on your role and plan. Contact your administrator—permissions are set in \"Settings → Team & Roles\".",
      },
    ],
  },
  "roles-and-access": {
    title: "Roles and Employee Access",
    intro:
      "How roles determine which sections are visible and what actions are allowed, and how to give an employee the right permissions.",
    updated: "June 2026",
    sections: [
      {
        heading: "How Access Works",
        body: [
          "Access is role-based: a role is a set of permissions (what's visible in the menu and what actions are allowed).",
          "Typical roles: account administrator, financial director, treasurer, procurement officer, financial controller, cost center manager, observer.",
        ],
      },
      {
        heading: "Who Can Do What",
        body: [
          "Create requests and documents—employees in the relevant section (treasurer, procurement officer).",
          "Approve (accept/reject)—those with approval rights (usually managers and financial control).",
          "Configure the system (roles, routes, integrations)—account administrator.",
        ],
        tip: "It's better to deactivate a fired employee than delete them—this preserves the history of their actions in documents.",
      },
    ],
    faq: [
      {
        q: "An employee can't approve a request",
        a: "Check that their role has approval rights and the request is actually pending approval, not in draft status.",
      },
    ],
  },
  "period-and-entity": {
    title: "Choosing Period and Switching Organizations",
    intro:
      "Most reports depend on the selected period and active legal entity. Let's see how this works.",
    updated: "June 2026",
    sections: [
      {
        heading: "Period",
        body: [
          "At the top of report pages is a period selector (month, quarter, year). When you change the period, data is recalculated.",
          "If the screen is empty—the first thing to check is usually the selected period: verify that it has data.",
        ],
      },
      {
        heading: "Multiple Legal Entities",
        body: [
          "If your company has several legal entities or objects, the active legal entity is switched in the top-right header.",
          "Documents and reports are shown in the context of the active legal entity—switch it to see data from another object.",
        ],
      },
    ],
    faq: [
      {
        q: "The screen is empty, but I know data exists",
        a: "Check the period at the top and the active legal entity in the header. Usually the data belongs to a different period or a different object.",
      },
    ],
  },
  "favorites-and-help": {
    title: "Favorites, Help, and Knowledge Base",
    intro:
      "How to pin frequently used screens and quickly get help directly from any section.",
    updated: "June 2026",
    sections: [
      {
        heading: "★ Favorites",
        body: [
          "The star in the header adds the current screen to favorites for quick access. Click again to remove it.",
        ],
      },
      {
        heading: "? Help and Knowledge Base",
        body: [
          "The \"?\" button in the header opens a context-sensitive tip for the section you're in.",
          "From the tip, you can jump to the full article or open the entire knowledge base—a section with all guides and search.",
        ],
        tip: "The knowledge base has search: enter \"limit\", \"1C\", \"approval\"—the system will show matching articles.",
      },
    ],
  },

  // Treasury
  "treasury-payment-request": {
    title: "Payment Request (ЗРДС)",
    intro:
      "Complete walkthrough of a payment request: which fields to fill in, how VAT and currency are calculated, what happens after approval.",
    updated: "June 2026",
    sections: [
      {
        heading: "When You Need a Request",
        body: [
          "A payment request is a request to pay a supplier or counterparty, which goes through approval before payment.",
          "A request can be created manually or automatically—for example, when receiving goods by purchase order or from a contract payment schedule.",
        ],
      },
      {
        heading: "How to Create a Request",
        body: [
          "Fill in the basic fields—the system will check required fields before you can submit.",
        ],
        steps: [
          "Open \"Treasury → New Request\".",
          "Select the counterparty and a line item for the payment (Cash Flow Statement line).",
          "Enter the amount, VAT rate, currency, and payment due date (deadline).",
          "If paying by contract—link the contract: the amount will count toward the contract's actual execution.",
          "Specify the cost center/project and payment purpose, save.",
        ],
        tip: "VAT is calculated from the amount including the tax (as in standard 1C forms): VAT = amount × rate / (100 + rate).",
      },
      {
        heading: "Urgency and Priority",
        body: [
          "Priority (\"urgent\", \"routine\", \"as scheduled\") affects the request's priority in the payment day register.",
          "The payment due date is used to track overdue payments—requests nearing the deadline are highlighted.",
        ],
      },
      {
        heading: "What Happens Next",
        body: [
          "After creation, the request goes to approval following the configured route.",
          "Approved requests go to \"Payment Day\", where they form the payment register.",
        ],
      },
    ],
    faq: [
      {
        q: "Can I pay in a foreign currency?",
        a: "Yes. Enter the currency and exchange rate—the amount will be automatically converted to your base accounting currency.",
      },
      {
        q: "The request won't submit for approval",
        a: "Check required fields (counterparty, line item, amount, due date). The system will highlight missing ones.",
      },
      {
        q: "How do I link a payment to a contract?",
        a: "In the request, select the contract in the corresponding field—the payment will increase the \"actual\" amount for that contract in its card.",
      },
    ],
  },
  "treasury-approvals": {
    title: "Approving Payment Requests",
    intro:
      "Approve or reject payment requests one-by-one or in bulk on Treasury → Approvals; My Tasks is an additional inbox.",
    updated: "July 2026",
    sections: [
      {
        heading: "Approval Inbox",
        body: [
          "Treasury → Approvals (/treasury/approvals) is the main inbox for payment requests pending your decision.",
          "My Tasks also supports bulk actions for mixed task types.",
          "Lists respect the legal-entity header filter (including All entities).",
        ],
        steps: [
          "Open Treasury → Approvals.",
          "Review amount, counterparty, line item, and purpose.",
          "Click Approve or Reject.",
          "When rejecting, provide a reason saved in history.",
        ],
      },
      {
        heading: "Bulk approval",
        body: [
          "On Approvals, select checkboxes on the current page and use Approve selected / Reject selected.",
          "Processing continues on errors; a summary shows successes and failures.",
          "My Tasks bulk is complementary—not the only place for bulk payment approvals.",
        ],
        tip: "Bulk is page-scoped; change pagination to select the next page.",
      },
      {
        heading: "Email notifications",
        body: [
          "Emails are sent only when SMTP is configured on the environment.",
        ],
        tip: "Without SMTP, notifications may log only—ask your admin.",
      },
    ],
    faq: [
      {
        q: "Where is bulk—My Tasks or Approvals?",
        a: "Primary place for payment requests is Treasury → Approvals. My Tasks is an additional inbox.",
      },
    ],
  },
  "treasury-payment-register": {
    title: "Payment Day and Payment Register",
    intro:
      "Canonical payment-day screen: date, reschedule, print register, payment-order draft. How to mark paid (primary vs import).",
    updated: "July 2026",
    sections: [
      {
        heading: "What's on Screen",
        body: [
          "The table shows requests in \"Approved\" status, ready for payment, and the total amount due for the day.",
        ],
      },
      {
        heading: "Printing the Register",
        body: [
          "The \"Print Register\" button creates a printable form with a list of requests, total, and signature lines for the treasurer and financial director.",
        ],
        steps: [
          "Open \"Treasury → Payment Day\".",
          "Review the register contents and amount.",
          "Click \"Print Register\" and print/save as PDF.",
          "After actual payment, mark the request as paid (see section below).",
        ],
      },
      {
        heading: "How to mark paid",
        body: [
          "Primary path: the Payment tab on the request card — full or partial payments (multiple payment lines).",
          "Secondary on this screen: Import payments — bulk selected rows, CSV, or match 1C bank outflows. Does not replace the card tab for a single request or partial pays.",
        ],
        tip: "Payment Day remains the canon for print and payment-order drafts; marking paid defaults to the card.",
      },
    ],
    faq: [
      {
        q: "Where do I mark a request as paid — day or card?",
        a: "Primary: Payment tab on the request card (including partial). Import on Payment Day is the secondary bulk/CSV/1C path.",
      },
    ],
  },
  "treasury-calendar": {
    title: "Payment Calendar and Cash Flow Gaps",
    intro: "How to plan payments by day and anticipate liquidity shortfalls.",
    updated: "July 2026",
    sections: [
      {
        heading: "Why You Need a Calendar",
        body: [
          "The calendar shows planned inflows and payments by day—helping you spot cash flow gaps before they happen.",
          "Spread payments across days based on expected inflows to avoid going negative.",
        ],
        tip: "If payments for a day exceed inflows, move non-urgent payments to later days.",
      },
      {
        heading: "No «Treasurer workspace» menu item",
        body: [
          "The separate «Treasurer workspace» nav item is hidden. Use overview CTAs «Due today» and «Calendar» on /treasury.",
          "Old bookmarks /treasury/desktop open the treasury overview (redirect). The daily work screen remains Payment Day.",
        ],
      },
    ],
  },

  // Procurement
  "procurement-overview": {
    title: "Procurement: Full Cycle from Request to Payment",
    intro:
      "Request → order → receiving → invoice → payment; draft/rejected orders are editable; related-docs panel and LE filter.",
    updated: "July 2026",
    sections: [
      {
        heading: "Cycle stages",
        body: ["Procurement moves through request, supplier order, receiving (GRN), invoice, and payment."],
        steps: [
          "Purchase request → approval.",
          "Supplier order (via New order).",
          "Receiving (full or partial).",
          "Supplier invoice.",
          "Payment request may be created on receiving.",
        ],
      },
      {
        heading: "Draft and rejected orders are editable",
        body: [
          "Open the card and fix header / BDR fields—do not delete and recreate for a cost-center typo.",
          "MVP limit: line items are not edited in this flow.",
        ],
        tip: "See the article Edit purchase order draft.",
      },
    ],
    faq: [
      {
        q: "Wrong cost center on a draft—must I delete?",
        a: "No. Open the draft or rejected order, edit, and save.",
      },
    ],
  },
  "procurement-receiving": {
    title: "Receiving Goods and Services",
    intro: "How to record receipt against an order and what happens to payment after receipt.",
    updated: "June 2026",
    sections: [
      {
        heading: "How to Record Receipt",
        body: [
          "Open an order and mark the goods/services actually received. The order moves to \"Received\" or \"Partially Received\".",
        ],
        steps: [
          "Open the order in \"Procurement → Orders\".",
          "Mark received line items and quantities.",
          "Save receipt—a payment obligation is created.",
          "The obligation goes to Treasury as a payment request.",
        ],
        tip: "If a shipment arrives partially—enter the actual quantity; you can receive the rest separately later.",
      },
    ],
  },

  // Contracts
  "contracts-overview": {
    title: "Contracts: How the Module Works",
    intro:
      "Overview: contract requests, register, approvals, income and expense contracts, budget vs. actual.",
    updated: "June 2026",
    sections: [
      {
        heading: "Module Sections",
        body: [
          "Overview—summary of requests and contracts (KPIs: total, pending, active, expiring; income vs. expense).",
          "Contract Requests—contract initiation and approval before signing.",
          "Register—contract list with filters by status, type, and direction.",
          "Approval—separate inboxes for requests and contracts.",
          "Canonical URLs — `/contracts/*`. Contract card: `/contracts/[id]`. Legacy `/procurement/contracts/[id]` redirects (bookmarks OK).",
        ],
      },
      {
        heading: "Correct Workflow",
        body: [
          "In the system, a contract doesn't appear out of nowhere—it's born from an approved request.",
        ],
        steps: [
          "A contract request is created (the initiator describes the need).",
          "The request is approved following the route.",
          "From the approved request, the contract itself is created.",
          "The contract is executed: payment schedule, payment tracking, budget vs. actual.",
        ],
      },
      {
        heading: "Budget vs. Actual by Contract",
        body: [
          "In the contract card, a \"Budget vs. Actual\" block shows planned amount, paid (from linked payment requests), and remaining balance.",
          "The actual amount grows as payments are made with a link to this contract.",
        ],
      },
    ],
    faq: [
      {
        q: "What's the difference between income and expense contracts?",
        a: "Expense—we pay the counterparty (purchases, services, rent). Income—they pay us (room rental, services to guests, etc.). The direction is set in the request and carried to the contract.",
      },
    ],
  },
  "contract-requests": {
    title: "Contract Request: Initiation and Approval",
    intro:
      "The key step in contracts: how to create a contract request, take it through approval, and turn it into a contract.",
    updated: "June 2026",
    sections: [
      {
        heading: "Why You Need a Request",
        body: [
          "A contract request captures the need: with whom, for what, and for roughly how much.",
          "First the request is approved—and only then is the contract itself created. This prevents signing unapproved contracts.",
        ],
      },
      {
        heading: "How to Create a Request",
        body: [
          "The counterparty can be \"planned\"—if the new supplier isn't in the directory yet, enter their name and tax ID manually.",
        ],
        steps: [
          "Open \"Contracts → Contract Requests → Create Request\".",
          "Fill in scope, direction (income/expense), counterparty, type, estimated amount.",
          "Specify cost center/project, BDR line item, timeline, justification, and urgency.",
          "Save and click \"Submit for Approval\".",
        ],
        tip: "If the counterparty isn't in the directory—select \"Planned\" and type their name and tax ID. You'll choose the actual counterparty when creating the contract.",
      },
      {
        heading: "Approval and Budget Check",
        body: [
          "The request follows the approval route (default: Cost Center Manager → Financial Control).",
          "The card shows a soft budget check: percent used and available balance by line item and cost center, with warnings if exceeded.",
        ],
      },
      {
        heading: "Creating a Contract from a Request",
        body: [
          "After request approval, you can turn it into a contract—details transfer automatically.",
        ],
        steps: [
          "Open the approved request (status \"Approved\").",
          "Click \"Create Contract from Request\".",
          "Enter contract number, counterparty (if it was \"planned\"), and signing date.",
          "The contract is created active; the request moves to \"Contract Created\" and links to it.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need to approve the contract separately later?",
        a: "No. By default, the request is approved; the contract is created already registered. Separate approval is only for amendments and changes.",
      },
      {
        q: "Where do I find the request status?",
        a: "Statuses: Draft → Pending Approval → Approved → (Contract Created) or Rejected. Visible in the register and in the request card.",
      },
      {
        q: "Can I edit a request after rejection?",
        a: "Yes. A rejected request can be revised and resubmitted for approval.",
      },
    ],
  },
  "contract-requests-approvals": {
    title: "Approving Contract Requests",
    intro:
      "Where to find contract requests for approval and how to process them.",
    updated: "June 2026",
    sections: [
      {
        heading: "Request Approval Inbox",
        body: [
          "The \"Contracts → Approve Requests\" section collects contract requests in \"Pending Approval\" status.",
        ],
        steps: [
          "Open \"Contracts → Approve Requests\".",
          "Check scope, counterparty, amount, and direction.",
          "Click \"Approve\" or \"Reject\" (with reason).",
        ],
        tip: "Action buttons are available only to those with approval rights. Without it, you'll see an \"Open\" button to view the card.",
      },
    ],
  },
  "contracts-approvals": {
    title: "Approving Contracts",
    intro:
      "Approving the contract itself (typically used for amendments and changes).",
    updated: "June 2026",
    sections: [
      {
        heading: "When It's Used",
        body: [
          "The main path is approving the contract request, after which the contract is created already registered.",
          "Separate contract approval is used for amendments and changes to the terms.",
        ],
      },
    ],
  },

  // Budget Control
  "budget-limits": {
    title: "Budget Limits by Cost Center and Line Item",
    intro:
      "How to upload planned limits, how actual execution is tracked, and what to do if there's no data for a period.",
    updated: "June 2026",
    sections: [
      {
        heading: "What's a Limit",
        body: [
          "A limit is a planned spending amount at the intersection of dimensions: period × cost center × line item (× project if needed).",
          "The system controls execution against the limit: how much was planned, reserved, spent, and how much is still available.",
        ],
      },
      {
        heading: "Importing Limits (Plan)",
        body: [
          "Planned limits are uploaded from Excel using a standard template.",
        ],
        steps: [
          "Open \"Budget Control → Budget Limits\".",
          "Click \"Import Limits (Plan)\" and download the template.",
          "Fill in periods, cost centers, line items, and amounts.",
          "Upload the file—limits appear in the table.",
        ],
        tip: "\"Import Limits (Plan)\" and \"Load Actuals (Execution)\" are different buttons and data. Don't mix them up: plan = what you can spend, actual = what you've already spent.",
      },
      {
        heading: "Loading Actuals (Execution)",
        body: [
          "The \"Load Actuals\" section uploads actual line-item transactions by cost center.",
          "After loading, the table updates columns for \"Actual\", \"Available\", and \"% Used\".",
        ],
      },
      {
        heading: "No Data for the Period",
        body: [
          "Check the period selector at the top of the table. If the plan isn't loaded—load it via \"Import Limits\".",
        ],
      },
    ],
    faq: [
      {
        q: "How does \"reserved\" differ from \"actual\"?",
        a: "Reserve is planned but not yet paid obligations (e.g., approved requests). Actual is money actually paid.",
      },
      {
        q: "Can I set a limit without a cost center?",
        a: "Yes. A limit without a cost center/project applies to the entire level above—a \"general\" line-item limit.",
      },
    ],
  },
  "budget-plan-vs-fact": {
    title: "Plan vs. Actual and Overrun Control",
    intro:
      "How to read \"Plan vs. Actual\" and \"Overruns\" reports and react quickly to budget overages.",
    updated: "July 2026",
    sections: [
      {
        heading: "Reading \"Plan vs. Actual\"",
        body: [
          "The report shows planned and actual amounts by line item, with variance (in rubles and percent). Negative variance on expenses is savings; positive is overspend.",
        ],
      },
      {
        heading: "Where to open (canonical nav)",
        body: [
          "Primary sidebar entry: Budget Control → Plan / Actual (`/reports/plan-vs-fact`). The duplicate under Reporting is hidden.",
          "Bookmarks and the soft link on the `/reports` hub still open the same report.",
        ],
      },
      {
        heading: "Overruns",
        body: [
          "The \"Overruns\" report highlights line items and cost centers where actual exceeded the limit—for quick response and root-cause analysis.",
        ],
        tip: "Review \"Overruns\" regularly (e.g., weekly) to catch variances before period-end, not after.",
      },
    ],
  },

  // Reports
  "management-accounting": {
    title: "Management Accounting: Overview and Reports",
    intro:
      "What the management accounting section shows and how to navigate to specialized reports.",
    updated: "June 2026",
    sections: [
      {
        heading: "Summary Metrics",
        body: [
          "The overview shows key metrics (revenue, expenses, profit, margin) and a month-over-month chart with year selection.",
        ],
      },
      {
        heading: "Specialized Reports",
        body: [
          "From here you navigate to P&L (income and expenses), Cash Flow Statement (money movement), Balance, and Budget (plan vs. execution).",
        ],
      },
    ],
  },
  "report-dds": {
    title: "Cash Flow Statement (ДДС)",
    intro:
      "How the Cash Flow Statement is structured by hotel standard: operating, investing, and financing activities.",
    updated: "June 2026",
    sections: [
      {
        heading: "Report Structure",
        body: [
          "Cash flow is split into three activity types (hotel standard USALI): operating, investing, and financing.",
          "P&L operating items map to operating activity; asset purchases to investing; loans and dividends to financing.",
        ],
        tip: "If a line is in the wrong activity category, that's set in line-item classification; contact your administrator.",
      },
    ],
  },
  "report-bdr": {
    title: "Budget of Income & Expenses (BDR) Turnover",
    intro:
      "Hierarchy of budget line items with monthly amounts and breakdown by documents.",
    updated: "June 2026",
    sections: [
      {
        heading: "Table Navigation",
        body: [
          "Click the arrow on the left to expand child line items. Toggle \"Drill-down\" and click a row to see the detailed breakdown by documents.",
        ],
      },
      {
        heading: "Loading Actuals",
        body: [
          "In the \"Load Actuals from Excel\" block, download the template, fill in actual amounts, and upload. This is actual data (not plan); after upload, \"Plan vs. Actual\" updates.",
        ],
      },
    ],
  },
  "usali-standard": {
    title: "Hotel Standard USALI",
    intro:
      "What USALI is, why you need it, and how it's used in system reports.",
    updated: "July 2026",
    sections: [
      {
        heading: "About the Standard",
        body: [
          "USALI is a unified management reporting system for hotels. Revenue and expenses are grouped by department (Schedules S1–S13).",
          "The standard ensures comparability with industry benchmarks (GOPPAR—profit per room, RevPAR—revenue per available room).",
        ],
        tip: "USALI matters if you compare your hotel's performance against market or within a chain—everyone uses the same rules.",
      },
      {
        heading: "Where in the menu",
        body: [
          "Under Reporting, the USALI / standard P&L pack is collapsed by default under “USALI (advanced)”. KPI and management accounting stay above.",
          "Expand the subheader or open a bookmark to `/usali` / `/finoko/…` — routes were not removed.",
        ],
      },
    ],
  },

  // Registers
  "registers-hub": {
    title: "Registers: Where to Find Organization Data",
    intro:
      "A hub with tabular data: turnovers, inventory balances, payables, with Excel export.",
    updated: "June 2026",
    sections: [
      {
        heading: "How to Use",
        body: [
          "Open the register card you need—you'll land on a ready screen with data for the selected period.",
          "Each register offers Excel/CSV export and, where applicable, drill-down to documents.",
          "NSI (Jul 2026): the sidebar shows a short list of frequent directories (counterparties, cost centers, bank accounts, articles, …). The full catalog—including rare types and enumerations—is the “All directories” hub (`/directories`).",
        ],
      },
      {
        heading: "Where Data Comes From",
        body: [
          "Some registers are populated by Excel uploads (bank statements, inventory balances, payables register)—until 1C is connected.",
          "After 1C integration, these update automatically.",
        ],
      },
    ],
  },
  "excel-import": {
    title: "Loading Data from Excel",
    intro:
      "Excel bridge until 1C is connected: what and where to upload, and how the template differs from exports.",
    updated: "June 2026",
    sections: [
      {
        heading: "How It Works",
        body: [
          "Wherever there's an \"Upload from Excel\" block, first download the standard template, fill it in, and upload.",
        ],
        steps: [
          "Click \"Download Template\" in the relevant section.",
          "Fill in data without changing the template column structure.",
          "Upload the file via \"Upload from Excel\".",
          "Verify the result in the table.",
        ],
        tip: "Actual load = real data (amounts, balances, statements), not plan. Don't mix with planned-limit import.",
      },
      {
        heading: "Where It's Available",
        body: [
          "BDR actuals—in \"Budget of Income & Expenses\"; limits and execution—in \"Budget Limits\"; bank statements and inventory—in their registers; reference data (cost centers, counterparties, projects)—import and template export.",
        ],
      },
    ],
    faq: [
      {
        q: "I uploaded a file—data didn't appear",
        a: "Check you didn't change column headers in the template and that you picked the right period. The system will show row-by-row errors if the format is wrong.",
      },
    ],
  },

  // Integrations
  "integration-1c": {
    title: "1C:Accounting Integration (OData)",
    intro:
      "Self-service 1C connection for automatic turnover, balance, and nomenclature uploads—and what to do if the connection fails.",
    updated: "June 2026",
    sections: [
      {
        heading: "What Integration Gives You",
        body: [
          "After 1C is connected, turnovers, balances, and nomenclature load automatically—no more Excel uploads.",
          "The \"Integrations\" section is available on Pro and Enterprise plans.",
        ],
      },
      {
        heading: "How to Connect",
        body: [
          "You'll need the OData service URL for 1C, login, password, and register name.",
        ],
        steps: [
          "Open \"Settings → Integrations → Connect 1C:Accounting\".",
          "Enter the OData service URL, login, password, and register name.",
          "Click \"Test Connection\"—the system will make a real request to your 1C.",
          "If the test passes, click \"Sync\".",
        ],
        tip: "\"Test Connection\" makes a live request to your 1C. If it passes, data is accessible; if not, see the section below.",
      },
      {
        heading: "If Connection Fails",
        body: [
          "Make sure your 1C has the OData service published (on IIS/Apache web server) and the user has read rights to the register.",
          "A typical HTTP 500 error is a 1C/web server issue, not Keiront. Your 1C admin fixes it by checking publication, user rights, and standard OData interface.",
        ],
      },
      {
        heading: "Account Mapping and Actual in Reports",
        body: [
          "After connecting, load the chart of accounts from 1C and set up mapping: which 1C account maps to which USALI line and with what sign (income +/expense −). The system suggests auto-mapping for revenue and expense accounts—you review and save.",
          "Once mapping is saved and journal entries are loaded, actual amounts from revenue/expense accounts appear in the USALI P&L report. Rules for balance-sheet accounts (cash, bank, inventory, payables) are saved and used in the Cash Flow Statement and balance stages.",
        ],
        tip: "Details are in \"1C Account Mapping Master → USALI\".",
      },
      {
        heading: "What is not production-ready yet",
        body: [
          "In Integrations nav, the working path is 1C:Accounting (OData) + hub; PMS / 1C:Hotel / Payroll / iiko / Opera / Logus / TravelLine / TNG are under a «Coming soon» section with a badge (config UI ≠ production ETL).",
          "Those setup screens stay reachable by URL but do not mean out-of-the-box data exchange without a separate project.",
        ],
      },
    ],
    related: ["1c-account-mapping", "registers-hub", "excel-import"],
    faq: [
      {
        q: "Is it safe to enter the 1C password?",
        a: "Yes. The password is encrypted and used only for requests to your 1C.",
      },
      {
        q: "Test connection gives HTTP 500",
        a: "Almost always a 1C side issue: the OData service isn't published or is published with an error. Have your 1C admin check publication on the web server and user permissions.",
      },
      {
        q: "Why doesn't actual appear in reports after connecting?",
        a: "You need account mapping (1C accounts → USALI lines) and journal entry load. See \"1C Account Mapping Master → USALI\".",
      },
    ],
  },
  "1c-account-mapping": {
    title: "1C Account Mapping Master → USALI",
    intro:
      "How to match your 1C chart of accounts to hotel-standard USALI lines so journal entries automatically roll up into Budget of Income & Expenses and P&L.",
    updated: "July 2026",
    sections: [
      {
        heading: "Why Mapping Is Needed",
        body: [
          "1C keeps records by its own chart of accounts (Russian GAAP), but management reporting for hotels is by USALI (by department: rooms, F&B, admin, marketing, etc.).",
          "Mapping is the rule: 1C account → USALI line + sign (income +/expense −). Journal entries from 1C automatically become budget actual by this rule.",
        ],
      },
      {
        heading: "How to Set Up",
        body: [
          "Open \"Settings → Account Mapping 1C → USALI\".",
        ],
        steps: [
          "Click \"Load Chart of Accounts from 1C\"—the account directory loads (read-only).",
          "Click \"Auto-Map All\"—the system suggests mappings for revenue and expense accounts.",
          "Check rows marked \"review\"—if needed, pick a different USALI line or sign.",
          "Click \"Save All\"—rules save and actual recalculates.",
        ],
        tip: "Auto-suggestions pick a default department (e.g., payroll → admin). If your setup differs, fix the USALI line by hand; after save, actual recalculates automatically.",
      },
      {
        heading: "What Gets Into Actuals",
        body: [
          "Budget of Income & Expenses actuals come from revenue and expense accounts (classes 90, 91, 20, 26, 44, 70, 69, etc.).",
          "Balance-sheet accounts (cash, bank, inventory, payables) don't go into P&L—their rules save and work at the Cash Flow Statement and balance stages.",
        ],
      },
      {
        heading: "Updating Actuals",
        body: [
          "Change mapping—actual updates automatically on save, or via \"Recalculate Actual\".",
          "Load journal entries for a period via \"Load Actual\"; data rolls into the USALI P&L report by the saved rules.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I have to map all 493 accounts?",
        a: "No. Only revenue and expense accounts go into Budget of Income & Expenses actuals—map those. Balance-sheet accounts can wait (for Cash Flow Statement/balance stage).",
      },
      {
        q: "I made a mapping mistake—how do I fix it?",
        a: "Change the USALI line for the account and save—actual recalculates idempotently, no duplication.",
      },
    ],
  },
  "settings-roles": {
    title: "Users and Roles",
    intro: "How to add an employee, assign a role, manage passwords, and properly deactivate a terminated employee.",
    updated: "September 2026",
    sections: [
      {
        heading: "Adding an Employee: Two Ways",
        body: [
          "Employees live in \"Settings → Team\". You can either create the account yourself or send an invitation.",
          "\"Create user\" — you set the full name, email, role, legal entities, and password right away, and the employee can sign in immediately. This is the main option when outgoing email is not configured.",
          "\"Invite user\" — the system issues a link, and the employee sets their own password. The link is valid for 72 hours.",
        ],
        steps: [
          "Open \"Settings → Team\".",
          "Click \"Create user\".",
          "Fill in the full name and email, then pick a role — it determines visible sections and allowed actions.",
          "Select the legal entities the employee works with: without them they can sign in but will see no data.",
          "Either keep \"Generate password automatically\" or set a password yourself.",
          "Keep \"Require password change on first sign-in\" enabled, so the employee sets their own password and nobody else knows it.",
          "Save and pass the email and password to the employee. A generated password is shown only once.",
        ],
        tip: "Deactivate terminated employees instead of deleting them—their action history in documents is preserved.",
      },
      {
        heading: "Passwords: Who Changes What",
        body: [
          "Employees change their own password themselves: their name in the top-right corner → \"My profile\" → \"Change password\". The current password is required; other devices are signed out afterwards.",
          "An administrator can set a new password for an employee (\"Set password\" in the employee row) — useful when someone lost access and waiting for email is not an option. The employee's active sessions end.",
          "\"Reset link\" keeps the password known only to the employee: the system issues a one-time link and the employee sets the password themselves.",
          "A password must be at least 8 characters long, contain both letters and digits, and must not be a common one (\"qwerty123\") or contain the person's own name or email.",
        ],
        tip: "If outgoing email is not configured for the organization, invitation and reset emails are not sent — the system shows the link on screen and you pass it on yourself. Configure it in \"Settings → Notifications\".",
      },
    ],
  },
  "settings-approval-rules": {
    title: "Approval Routes",
    intro:
      "How to configure approval stages for requests and contracts—by amount, line item, or department.",
    updated: "June 2026",
    sections: [
      {
        heading: "How Routes Work",
        body: [
          "A route describes the approval sequence based on conditions (e.g., payment amount).",
          "Documents (payment requests, orders, contract requests) follow the matching route automatically.",
        ],
      },
      {
        heading: "Multi-Step Routes",
        body: [
          "You can set multiple sequential steps—for example, contract requests by default: Cost Center Manager → Financial Control. The document is approved after all steps.",
        ],
        tip: "Set different routes for different amounts: small = one step, large = with finance director involvement.",
      },
    ],
  },
  "demo-data": {
    title: "Demo Data",
    intro:
      "How to load example data into an empty account for a product demo—and how it differs from real data.",
    updated: "June 2026",
    sections: [
      {
        heading: "Why You Need It",
        body: [
          "On a new (empty) account, dashboards have no data. To show what the system looks like with data, you can load a demo set once.",
          "This is demo data, not real organization data. For real work, load your own data from Excel or connect 1C.",
        ],
      },
      {
        heading: "How to Load",
        body: [
          "Available to account administrators.",
        ],
        steps: [
          "Open \"Settings → Demo Data\".",
          "See the current dashboard fill level.",
          "Click \"Load Demo Data\" and confirm.",
          "Open dashboards—they'll be populated with examples.",
        ],
        tip: "This button is for empty or demo accounts. Loading demo data onto a real-data account will add examples on top—be careful.",
      },
    ],
    faq: [
      {
        q: "Will real clients accidentally see demo data?",
        a: "No. Demo data doesn't load automatically—only if an admin clicks the button explicitly. New accounts start clean.",
      },
    ],
  },

  "procurement-order-draft-edit": {
    title: "Editing a Purchase Order Draft",
    intro: "How to fix draft or rejected POs without recreating them; MVP limits on line items.",
    updated: "July 2026",
    sections: [
      {
        heading: "How to edit",
        body: ["Open Orders, open a Draft or Rejected card, change header/BDR fields, save, resubmit if needed."],
        tip: "Line items are out of scope for this MVP edit path.",
      },
    ],
  },
  "related-documents": {
    title: "Related Documents on a Card",
    intro: "Related-docs panel on PO, contract request, and GRN cards is the canonical chain UX (sidebar Document chain hidden).",
    updated: "July 2026",
    images: [
      {
        src: "/help/sc-proc-03-related-docs-schema.svg",
        alt: "Schema: related documents panel on a PO card",
        caption: "Schema: where to find Related documents (not a production screenshot)",
      },
      {
        src: "/help/sc-proc-02-po-card-bottom.png",
        alt: "Lower part of a PO card — related-docs context",
        caption: "PO card context (live); related panel appears when document links are filled",
      },
    ],
    sections: [
      {
        heading: "Where to look",
        body: ["Open the Related documents panel on PO / CR / GRN cards to see the linked chain."],
      },
      {
        heading: "Canonical instead of /chain",
        body: [
          "The sidebar Document chain item (/procurement/chain UUID tool) is hidden.",
          "Old bookmarks redirect to the procurement overview; use the card panel instead.",
        ],
      },
    ],
  },
  "report-kpi-overview": {
    title: "KPI Overview (Operational Metrics)",
    intro: "Reporting → KPI Overview (/kpi): operational metrics hub, distinct from budget-control dashboard.",
    updated: "July 2026",
    sections: [
      {
        heading: "Where to open",
        body: ["Sidebar → Reporting → KPI Overview. Subpages cover trends, ranking, waterfall, etc."],
      },
    ],
  },
  "treasury-payment-order-1c": {
    title: "Payment Orders: File and 1C",
    intro: "Download a payment-order draft from Payment Day; upload into 1C may still be a separate human/pilot step.",
    updated: "July 2026",
    sections: [
      {
        heading: "Boundary",
        body: [
          "Keiront confirms file download. Automatic upload to 1C may still be WAITING:HUMAN on the pilot.",
          "Do not confuse with OData sync-now for directories/postings.",
        ],
      },
    ],
  },

};
